import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  LabelList,
  Cell,
} from 'recharts';
import { Globe, CalendarDays, MonitorPlay, Clapperboard } from 'lucide-react';
import { Select, PlatformIcon } from '@/components/primitives';
import type { SelectOption } from '@/components/primitives';
import { SegmentedControl } from '@/components/organisms';
import { ChartLegend } from '@/components/organisms/ChartLegend';
import { ChartTooltip } from '@/components/organisms/ChartTooltip';
import { Spinner } from '@/components/primitives';
import { apiUrl, getJson } from '@/lib/api';
import {
  fetchCatalog,
  exclusiveShare,
  MEASURE_META,
  type CatalogMeasure,
  type CatalogPlatformSplit,
  type CatalogQuarterSplit,
} from '@/lib/catalog';
import { formatUsd, formatCount, formatShare, shortPlatform, prettyQuarter } from '@/lib/format';
import styles from './PlatformCatalog.module.css';

// ─── Series colours (tokens; flip with theme automatically) ──────────────────
// Exclusive is the story, so it gets the brand gold; the non-exclusive remainder
// is a muted neutral. Stacked, gold sits at the base so the exclusive share reads
// as a proportion of the whole bar.
const COLOR_EXCLUSIVE = 'var(--accent-secondary)';
const COLOR_REMAINDER = 'var(--data-1)';
const MUTED_OPACITY = 0.28;

// ─── Filter options (platform/region) come from the same source as the rest ──
interface PlatformFilters {
  platforms: string[];
  regions: string[];
  quarters: string[];
}

function usePlatformFilters() {
  return useQuery({
    queryKey: ['platform-filters'],
    queryFn: () => getJson<PlatformFilters>(apiUrl('/platforms/filters')),
    staleTime: 10 * 60 * 1000,
  });
}

// ─── Measure formatting ──────────────────────────────────────────────────────
function formatMeasure(value: number, measure: CatalogMeasure): string {
  if (value == null) return '—';
  switch (measure) {
    case 'revenue':
      return formatUsd(value);
    case 'subscribers_equivalent':
      return formatCount(value);
    case 'titles':
      return value.toLocaleString();
  }
}

/* Stacked rows carry the gold "exclusive" base + the "remainder" (full − exclusive),
   so bar height = full catalogue and the gold portion = the exclusive share. We keep
   `full` and `share` around for labels and tooltips. */
interface StackDatum {
  key: string; // platform (macro) or quarter (micro)
  label: string;
  exclusive: number;
  remainder: number;
  full: number;
  share: number | null; // exclusive / full
}

function toStackData<T extends CatalogPlatformSplit | CatalogQuarterSplit>(
  rows: T[],
  measure: CatalogMeasure,
  keyOf: (r: T) => string,
  labelOf: (r: T) => string,
): StackDatum[] {
  return rows.map((r) => {
    const exclusive = r.exclusive[measure];
    const full = r.full[measure];
    return {
      key: keyOf(r),
      label: labelOf(r),
      exclusive,
      remainder: Math.max(full - exclusive, 0),
      full,
      share: exclusiveShare(r, measure),
    };
  });
}

// ─── Custom tooltip (tokenised) — shows exclusive, full, and the share ────────
function StackTooltip({
  active,
  payload,
  label,
  measure,
}: {
  active?: boolean;
  payload?: Array<{ payload: StackDatum }>;
  label?: string;
  measure: CatalogMeasure;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const d = payload[0].payload;
  return (
    <ChartTooltip
      title={label}
      rows={[
        { label: 'Exclusive', value: formatMeasure(d.exclusive, measure), color: COLOR_EXCLUSIVE },
        { label: 'Non-exclusive', value: formatMeasure(d.remainder, measure), color: COLOR_REMAINDER },
      ]}
      totals={[
        { label: 'Total catalogue', value: formatMeasure(d.full, measure) },
        ...(d.share != null ? [{ label: 'Exclusive share', value: formatShare(d.share), emphasis: 'secondary' as const }] : []),
      ]}
    />
  );
}

/* % label drawn above each stack. Recharts passes bar geometry; we read the datum's
   share off the chart data by index. */
function makeShareLabel(data: StackDatum[]) {
  return function ShareLabel(props: {
    x?: number;
    y?: number;
    width?: number;
    index?: number;
  }) {
    const { x = 0, y = 0, width = 0, index = 0 } = props;
    const share = data[index]?.share;
    if (share == null) return null;
    return (
      <text
        x={x + width / 2}
        y={y - 6}
        textAnchor="middle"
        className={styles.shareLabel}
        fill="var(--text-secondary)"
      >
        {Math.round(share * 100)}%
      </text>
    );
  };
}

// ─── Stacked bar chart (shared by macro + micro) ──────────────────────────────
function StackedCatalogChart({
  icon,
  title,
  data,
  measure,
  highlight,
  tickAngle = -35,
  height = 300,
}: {
  icon: React.ReactNode;
  title: string;
  data: StackDatum[];
  measure: CatalogMeasure;
  /** Macro only: the key (platform) to emphasise; everything else is muted. */
  highlight?: string;
  tickAngle?: number;
  height?: number;
}) {
  const dim = (key: string) => (highlight && key !== highlight ? MUTED_OPACITY : 1);
  const shareLabel = makeShareLabel(data);

  return (
    <div className={styles.chartCard}>
      <h3 className={styles.chartTitle}>
        <span className={styles.chartTitleIcon} aria-hidden="true">
          {icon}
        </span>
        {title}
      </h3>
      {data.length === 0 ? (
        <p className={styles.empty}>No data for this selection.</p>
      ) : (
        <ResponsiveContainer width="100%" height={height}>
          <BarChart data={data} margin={{ top: 20, right: 8, bottom: 4, left: 0 }}>
            <CartesianGrid vertical={false} stroke="var(--border-subtle)" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
              interval={0}
              angle={tickAngle}
              textAnchor="end"
              height={64}
              tickLine={false}
              axisLine={{ stroke: 'var(--border-subtle)' }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--text-muted)' }}
              tickFormatter={(v) => formatMeasure(v, measure)}
              tickLine={false}
              axisLine={false}
              width={56}
            />
            <RTooltip cursor={{ fill: 'var(--bg-hover)' }} content={<StackTooltip measure={measure} />} />
            <Bar stackId="catalog" name="Exclusive" dataKey="exclusive" fill={COLOR_EXCLUSIVE}>
              {data.map((d) => (
                <Cell key={d.key} fillOpacity={dim(d.key)} />
              ))}
            </Bar>
            <Bar
              stackId="catalog"
              name="Non-exclusive"
              dataKey="remainder"
              fill={COLOR_REMAINDER}
              radius={[2, 2, 0, 0]}
            >
              {data.map((d) => (
                <Cell key={d.key} fillOpacity={dim(d.key) * 0.85} />
              ))}
              <LabelList dataKey="share" content={shareLabel as never} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

// ─── Headline takeaway: exclusives as a share, and whether it's growing ───────
function Takeaway({
  platform,
  macroShow,
  microShow,
  measure,
  quarterLabel,
}: {
  platform: string;
  macroShow: CatalogPlatformSplit | undefined;
  microShow: CatalogQuarterSplit[];
  measure: CatalogMeasure;
  quarterLabel: string;
}) {
  if (!macroShow) return null;
  const nowShare = exclusiveShare(macroShow, measure);
  if (nowShare == null) return null;

  const earliest = microShow[0];
  const thenShare = earliest ? exclusiveShare(earliest, measure) : null;

  let trend: string | null = null;
  if (thenShare != null && earliest) {
    const deltaPts = (nowShare - thenShare) * 100;
    const dir = deltaPts >= 0.5 ? 'up' : deltaPts <= -0.5 ? 'down' : 'flat';
    if (dir === 'flat') {
      trend = `holding near ${formatShare(thenShare)} since ${prettyQuarter(earliest.endDate)}`;
    } else {
      trend = `${dir === 'up' ? 'up' : 'down'} from ${formatShare(thenShare)} in ${prettyQuarter(
        earliest.endDate,
      )}`;
    }
  }

  const noun = MEASURE_META[measure].short.toLowerCase();
  return (
    <p className={styles.takeaway}>
      In <strong>{quarterLabel}</strong>, exclusive {noun} is{' '}
      <strong className={styles.takeawayPct}>{formatShare(nowShare)}</strong> of{' '}
      <strong>{shortPlatform(platform)}</strong>’s TV catalogue
      {trend ? <> — {trend}</> : null}.
    </p>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export function PlatformCatalog() {
  const { data: filters } = usePlatformFilters();

  const [platform, setPlatform] = useState('Netflix');
  const [region, setRegion] = useState(''); // '' = all regions
  const [endDate, setEndDate] = useState(''); // '' = latest
  const [measure, setMeasure] = useState<CatalogMeasure>('revenue');

  const { data, isLoading, error } = useQuery({
    queryKey: ['catalog', platform, region, endDate],
    queryFn: () => fetchCatalog({ platform, region, endDate, lastQuarters: 12 }),
    staleTime: 5 * 60 * 1000,
  });

  const platformOptions: SelectOption[] = useMemo(
    () =>
      (filters?.platforms ?? []).map((p) => ({
        value: p,
        label: shortPlatform(p),
        icon: <PlatformIcon name={p} size={18} />,
      })),
    [filters],
  );

  const regionOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: 'All Regions' },
      ...(filters?.regions ?? []).map((r) => ({ value: r, label: r })),
    ],
    [filters],
  );

  // The "latest" option (value '') resolves to a real quarter the backend reports.
  // Spell it out — "Q1 2026" — rather than the vague "Latest Quarter".
  const latestLabel = data?.macro.quarter
    ? `${prettyQuarter(data.macro.quarter)} (latest)`
    : 'Latest Quarter';

  const quarterOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: latestLabel },
      ...(filters?.quarters ?? []).map((q) => ({ value: q, label: prettyQuarter(q) })),
    ],
    [filters, latestLabel],
  );

  const selectedPlatform = data?.micro.platform ?? platform;

  // Macro: sort by exclusive share desc so "who leans hardest on exclusives" reads
  // top-down. Keeps a stable order as you flip quarters (sorted by the metric, not name).
  const macroShow = useMemo(() => {
    if (!data) return [];
    const rows = toStackData(data.macro.show, measure, (r) => r.platform, (r) => shortPlatform(r.platform));
    return [...rows].sort((a, b) => (b.share ?? -1) - (a.share ?? -1));
  }, [data, measure]);

  const macroMovie = useMemo(() => {
    if (!data) return [];
    const rows = toStackData(data.macro.movie, measure, (r) => r.platform, (r) => shortPlatform(r.platform));
    return [...rows].sort((a, b) => (b.share ?? -1) - (a.share ?? -1));
  }, [data, measure]);

  const microShow = useMemo(
    () =>
      data
        ? toStackData(data.micro.show, measure, (r) => r.endDate, (r) => prettyQuarter(r.endDate))
        : [],
    [data, measure],
  );
  const microMovie = useMemo(
    () =>
      data
        ? toStackData(data.micro.movie, measure, (r) => r.endDate, (r) => prettyQuarter(r.endDate))
        : [],
    [data, measure],
  );

  const macroShowSplit = data?.macro.show.find((r) => r.platform === selectedPlatform);
  const quarterLabel = data?.macro.quarter ? prettyQuarter(data.macro.quarter) : 'the latest quarter';
  const measureLabel = MEASURE_META[measure].label.toLowerCase();

  return (
    <div className={styles.page}>
      {/* Controls */}
      <header className={styles.controls}>
        <div className={styles.selects}>
          <Select
            value={platform}
            options={platformOptions}
            onChange={setPlatform}
            searchable
            icon={<PlatformIcon name={platform} size={18} />}
            className={styles.select}
          />
          <Select
            value={region}
            options={regionOptions}
            onChange={setRegion}
            icon={<Globe size={14} />}
            onClear={() => setRegion('')}
            className={styles.select}
          />
          <Select
            value={endDate}
            options={quarterOptions}
            onChange={setEndDate}
            icon={<CalendarDays size={14} />}
            onClear={() => setEndDate('')}
            className={styles.select}
          />
        </div>
        <SegmentedControl
          value={measure}
          onChange={(v) => setMeasure(v as CatalogMeasure)}
          options={[
            { value: 'revenue', label: 'Revenue' },
            { value: 'subscribers_equivalent', label: 'Subs Equiv.' },
            { value: 'titles', label: 'Titles' },
          ]}
        />
      </header>

      {/* Shared legend — one for the whole page, not per chart */}
      <ChartLegend
        items={[
          { label: 'Exclusive', color: COLOR_EXCLUSIVE },
          { label: 'Non-exclusive', color: COLOR_REMAINDER },
        ]}
        note="Bar height = total catalogue · % = exclusive share"
      />

      {isLoading && (
        <div className={styles.loading}>
          <Spinner />
        </div>
      )}

      {error && (
        <p className={styles.errorMsg}>Couldn’t load catalog data. {(error as Error).message}</p>
      )}

      {data && !isLoading && (
        <>
          <Takeaway
            platform={selectedPlatform}
            macroShow={macroShowSplit}
            microShow={data.micro.show}
            measure={measure}
            quarterLabel={quarterLabel}
          />

          {/* Macro — the whole market, one quarter */}
          <section className={styles.section}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>Who leans hardest on exclusives</h2>
              <p className={styles.sectionMeta}>
                Every platform’s {measureLabel} in {quarterLabel} · {region || 'all regions'} · ranked by
                exclusive share · <strong className={styles.sectionAccent}>{shortPlatform(selectedPlatform)}</strong>{' '}
                highlighted
              </p>
            </div>
            <div className={styles.chartRow}>
              <StackedCatalogChart
                icon={<MonitorPlay size={15} />}
                title="TV Shows"
                data={macroShow}
                measure={measure}
                highlight={selectedPlatform}
              />
              <StackedCatalogChart
                icon={<Clapperboard size={15} />}
                title="Movies"
                data={macroMovie}
                measure={measure}
                highlight={selectedPlatform}
              />
            </div>
          </section>

          {/* Micro — one platform over time */}
          <section className={styles.section}>
            <div className={styles.sectionHead}>
              <h2 className={styles.sectionTitle}>
                {shortPlatform(selectedPlatform)}: is the exclusive bet growing?
              </h2>
              <p className={styles.sectionMeta}>
                {measureLabel} over the last 12 quarters · {region || 'all regions'}
              </p>
            </div>
            <div className={styles.chartRow}>
              <StackedCatalogChart
                icon={<MonitorPlay size={15} />}
                title="TV Shows"
                data={microShow}
                measure={measure}
                tickAngle={-45}
                height={280}
              />
              <StackedCatalogChart
                icon={<Clapperboard size={15} />}
                title="Movies"
                data={microMovie}
                measure={measure}
                tickAngle={-45}
                height={280}
              />
            </div>
          </section>
        </>
      )}
    </div>
  );
}
